import React from "react";
import { Modal, Button } from "react-bootstrap";
import { FaExclamationTriangle, FaTimes, FaCheck } from "react-icons/fa";

/**
 * Modal genérico de confirmación para eliminación de registros
 * 
 * @param {boolean} show - Controla si el modal está visible
 * @param {function} onHide - Función para cerrar el modal
 * @param {function} onConfirm - Función que se ejecuta al confirmar
 * @param {string} title - Título del modal (opcional, por defecto "Confirmar Eliminación")
 * @param {string} message - Mensaje de confirmación
 * @param {string} confirmText - Texto del botón de confirmar (opcional, por defecto "Eliminar")
 * @param {string} cancelText - Texto del botón cancelar (opcional, por defecto "Cancelar")
 * @param {boolean} loading - Indica si está procesando la eliminación
 * @param {string} variant - Variante del botón de confirmar: "danger" (por defecto) o "warning"
 */
const ModalConfirmacion = ({
  show,
  onHide,
  onConfirm,
  title = "Confirmar Eliminación",
  message,
  confirmText = "Eliminar",
  cancelText = "Cancelar",
  loading = false,
  variant = "danger",
}) => {
  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <>
      <style>
        {`
          .modal-confirmacion {
            z-index: 1055;
          }

          .modal-confirmacion .modal-content {
            border-radius: 20px;
            overflow: hidden;
            border: none;
            box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
          }

          .modal-confirmacion .modal-header {
            background: ${variant === "danger" 
              ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" 
              : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"};
            color: white;
            border: none;
            padding: 1.5rem 2rem;
          }

          .modal-confirmacion .modal-title {
            font-weight: 700;
            font-size: 1.25rem;
            display: flex;
            align-items: center;
            gap: 0.75rem;
          }

          .modal-confirmacion .btn-close {
            filter: brightness(0) invert(1);
            opacity: 0.9;
          }

          .modal-confirmacion .btn-close:hover {
            opacity: 1;
          }

          .modal-confirmacion .modal-body {
            padding: 2rem;
          }

          .modal-confirmacion .confirm-icon {
            font-size: 3rem;
            color: ${variant === "danger" ? "#ef4444" : "#f59e0b"};
            margin-bottom: 1rem;
          }

          .modal-confirmacion .confirm-message {
            font-size: 1.1rem;
            color: #374151;
            line-height: 1.6;
            margin: 0;
          }

          .modal-confirmacion .modal-footer {
            border-top: 2px solid #e5e7eb;
            padding: 1.5rem 2rem;
            background: #f9fafb;
          }

          .btn-confirm-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 24px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
            transition: all 0.3s ease;
          }

          .btn-confirm-danger:hover:not(:disabled) {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
            color: white;
          }

          .btn-confirm-danger:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .btn-confirm-warning {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 24px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(245, 158, 11, 0.3);
            transition: all 0.3s ease;
          }

          .btn-confirm-warning:hover:not(:disabled) {
            background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(245, 158, 11, 0.4);
            color: white;
          }

          .btn-confirm-warning:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .btn-cancel-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 24px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }

          .btn-cancel-secondary:hover:not(:disabled) {
            background: linear-gradient(135deg, #4b5563 0%, #374151 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
        `}
      </style>

      <Modal
        show={show}
        onHide={onHide}
        centered
        className="modal-confirmacion"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>
            <FaExclamationTriangle />
            {title}
          </Modal.Title>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={onHide}
            disabled={loading}
            aria-label="Cerrar"
          />
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <FaExclamationTriangle className="confirm-icon" />
            <p className="confirm-message">{message}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn-cancel-secondary d-flex align-items-center"
            onClick={onHide}
            disabled={loading}
          >
            <FaTimes className="me-2" />
            {cancelText}
          </Button>
          <Button
            className={`btn-confirm-${variant} d-flex align-items-center`}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Procesando...
              </>
            ) : (
              <>
                <FaCheck className="me-2" />
                {confirmText}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ModalConfirmacion;

